import { Autocomplete, CircularProgress } from '@mui/material';
import {
  stripZeroWidthChars,
  TextField,
  type TextFieldProps,
} from '@side/pantry';
import { get as lodashGet } from 'lodash';
import { type FocusEvent, useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useAutofillActionProvider } from 'src/views/Coversheet/hooks/useAutofillActionProvider';
import {
  type GooglePlacesResult,
  getAddressDetails,
  handleAddressDetailsError,
} from 'src/views/Coversheet/utils/googlePlaces';
import type { InteractionType } from '@/common/types/interaction';
import { useAppFlags } from '@/modules/launch-darkly/app-flags';
import {
  GooglePlacesAutofillOption,
  getGooglePlacesAutofillOptionKey,
} from '@/views/Coversheet/components/PlacesAutofill/GooglePlacesAutofillOption';
import { AutofillProvider } from '@/views/Coversheet/components/PlacesAutofill/PlacesAutofill.helpers';
import {
  TrackInteractionsEvent,
  useTrackInteractionsAnalytics,
} from '$analytics/useTrackAnalytics/domains/interactions';
import { TrackOfferEvent } from '$analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from '$analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import {
  getPredictionMatchKey,
  useMatchingListings,
} from './useMatchingListings';

type Props = {
  handleAutocompleteSelect: (value: GooglePlacesResult) => void;
  name: string;
  required: boolean;
  label: string;
  type?: InteractionType;
  onInputBlur?: (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  enableListingMatch?: boolean;
  limitToLicensedStates?: boolean;
} & Pick<TextFieldProps, 'highlight'>;

export const StreetAutocomplete = ({
  handleAutocompleteSelect,
  highlight,
  name,
  required,
  label,
  type,
  onInputBlur,
  enableListingMatch,
  limitToLicensedStates = true,
}: Props) => {
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    rules: {
      required: required ? 'Required' : false,
    },
  });
  const { getValues, setValue } = useFormContext();
  const { representationType } = getValues();

  const { txmLinkOffersListings } = useAppFlags();
  const listingMatchEnabled = !!enableListingMatch && !!txmLinkOffersListings;
  const listingMatchMap = useMatchingListings(
    field.value as string | undefined,
    listingMatchEnabled,
  );

  const [autofillOptions, setAutofillOptions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [loading, setLoading] = useState(false);
  const searchProvider = useAutofillActionProvider(
    AutofillProvider.googlePlacesSearch,
    { limitToLicensedStates },
  );

  const trackOfferEvent = useTrackOfferAnalytics();

  // Options load asynchronously while the dropdown stays open, so track each
  // matched listing once as it becomes visible rather than on every re-render
  // of the options list. The set resets when the dropdown closes so a listing
  // is counted again the next time it is shown.
  const [listboxOpen, setListboxOpen] = useState(false);
  const trackedMatchKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!listboxOpen || !listingMatchEnabled) return;
    for (const option of autofillOptions) {
      const matchKey = getPredictionMatchKey(option);
      const matchedListingId = listingMatchMap.get(matchKey);
      if (matchedListingId && !trackedMatchKeys.current.has(matchKey)) {
        trackedMatchKeys.current.add(matchKey);
        trackOfferEvent(TrackOfferEvent.ShowMyListingResult, {
          listingId: matchedListingId,
        });
      }
    }
  }, [
    listboxOpen,
    listingMatchEnabled,
    autofillOptions,
    listingMatchMap,
    trackOfferEvent,
  ]);
  const handleAutocompleteChange = async (
    newValue: google.maps.places.AutocompletePrediction,
  ) => {
    try {
      const result: GooglePlacesResult = await getAddressDetails(newValue);
      handleAutocompleteSelect(result);
    } catch (error) {
      handleAddressDetailsError(error);
    }
  };

  const trackEvent = useTrackInteractionsAnalytics();

  const handleFieldSelect = (e: FocusEvent<HTMLInputElement>) => {
    trackEvent(TrackInteractionsEvent.SelectField, {
      eventContainer: `${type}_address_card`,
      eventTarget: `location.${e.target.name}`,
      selected_input: e.target.value,
      representationType,
    });
  };

  const handleOnInputChange = (input: string) => {
    setLoading(true);
    searchProvider(input, (response) => {
      setAutofillOptions(response);
      setLoading(false);
    });
  };

  return (
    <Autocomplete
      id="google-map"
      getOptionLabel={(option) =>
        typeof option === 'string'
          ? option
          : option.structured_formatting.main_text
      }
      getOptionDisabled={(option) => !!lodashGet(option, 'noResults')}
      filterOptions={(x) => x}
      options={autofillOptions}
      autoComplete
      freeSolo
      fullWidth
      includeInputInList
      filterSelectedOptions
      inputValue={(field.value as string | undefined) ?? ''}
      onFocus={handleFieldSelect}
      onOpen={() => setListboxOpen(true)}
      onClose={() => {
        setListboxOpen(false);
        trackedMatchKeys.current.clear();
      }}
      onChange={(_, newValue) => {
        if (newValue !== null && typeof newValue !== 'string') {
          void handleAutocompleteChange(newValue);
          field.onChange(
            stripZeroWidthChars(newValue.structured_formatting.main_text),
          );
          if (listingMatchEnabled) {
            const matchedListingId = listingMatchMap.get(
              getPredictionMatchKey(newValue),
            );
            setValue('listingId', matchedListingId, { shouldDirty: true });
            if (matchedListingId) {
              trackOfferEvent(TrackOfferEvent.SelectMyListingResult, {
                listingId: matchedListingId,
              });
            }
          }
        }
      }}
      onInputChange={(_, newInputValue, reason) => {
        // `reason === 'reset'` fires when MUI internally syncs its input on
        // mount (freeSolo + no selected value) — propagating it would
        // clobber the controlled form value with an empty string.
        if (reason === 'reset') return;
        field.onChange(stripZeroWidthChars(newInputValue));
        void handleOnInputChange(newInputValue);
        // Editing the address by hand invalidates any previously linked
        // listing — the link is re-established only by selecting a match.
        if (listingMatchEnabled && reason === 'input') {
          setValue('listingId', undefined, { shouldDirty: true });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fixedHeight
          highlight={highlight}
          label={label}
          required={required}
          error={Boolean(isTouched && error)}
          helperText={isTouched && error ? error.message : ''}
          {...field}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: loading ? (
                <CircularProgress sx={{ mr: 2, color: 'inherit' }} size={20} />
              ) : (
                params.slotProps.input.endAdornment
              ),
            },
          }}
          onBlur={(e) => {
            field.onBlur();
            onInputBlur?.(e);
          }}
        />
      )}
      renderOption={(optionProps, option) => (
        <GooglePlacesAutofillOption
          key={getGooglePlacesAutofillOptionKey(option)}
          optionProps={optionProps}
          option={option}
          noResultsText={
            limitToLicensedStates
              ? 'Add more details and ensure the address is in the selected state'
              : 'Add more details'
          }
          showListingChip={
            listingMatchEnabled &&
            listingMatchMap.has(getPredictionMatchKey(option))
          }
        />
      )}
    />
  );
};
